import type { FC, KeyboardEventHandler, ReactElement } from 'react';
import { FormattedMessage } from 'react-intl';
import {
    AvailabilityMessage,
    BackorderThresholdMessage,
} from '../../../../components/quantity-messages';
import { LimitedQuantityLabel, OnlyXLeftLabel } from '../../../../components/scarcity-labels';
import { usePDP } from '../../../../contexts/pdp/usePDP';
import { QuantityInput, Text } from '../../../../ui-components';
import { MAX_QUANTITY_INPUT_NUMBER } from '../../../../ui-components/inputs/quantity-input/const';

interface Props {
    onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
    className?: string;
}

export const QuantityBox: FC<Props> = ({ onKeyDown, className }) => {
    const {
        selectedQuantity,
        setSelectedQuantity,
        inventoryStore: { flags, currentInventory },
        selectedShippingOptionType,
        availableQuantityToAdd,
    } = usePDP();

    const {
        hasEnoughStock,
        includesItemsInBackorder,
        isLimitedQuantityBadge,
        isOnlyXLeftBadge,
        isOnlyBackOrder,
    } = flags;
    const { availableToSell, allocatedStock, backorderInStockDate } = currentInventory;

    const showMessage =
        availableQuantityToAdd !== null ||
        (availableToSell > 0 && (!hasEnoughStock || includesItemsInBackorder));
    const showBadges =
        availableQuantityToAdd === null &&
        availableToSell > 0 &&
        (isOnlyXLeftBadge || isLimitedQuantityBadge);

    let maxValue = 0;
    if (selectedShippingOptionType) {
        maxValue =
            availableToSell > MAX_QUANTITY_INPUT_NUMBER
                ? MAX_QUANTITY_INPUT_NUMBER
                : availableToSell;
    }

    return (
        <div className={`flex items-center justify-between ${className ?? ''}`}>
            <QuantityInput
                id="quantity-box-product-quantity"
                onChange={setSelectedQuantity}
                onKeyDown={onKeyDown}
                value={selectedQuantity}
                maxValue={maxValue}
            />
            {showMessage
                ? renderMessages(
                      hasEnoughStock,
                      availableToSell,
                      includesItemsInBackorder,
                      isOnlyBackOrder,
                      backorderInStockDate,
                      allocatedStock,
                      selectedQuantity,
                      availableQuantityToAdd,
                  )
                : null}
            {showBadges && !showMessage
                ? renderBadges(availableToSell, isLimitedQuantityBadge, isOnlyXLeftBadge)
                : null}
        </div>
    );
};

QuantityBox.defaultProps = {
    onKeyDown: undefined,
    className: undefined,
};

function renderMessages(
    hasEnoughStock: boolean | undefined,
    availableToSell: number,
    includesItemsInBackorder: boolean | undefined,
    isOnlyBackOrder: boolean | undefined,
    backorderInStockDate: number | undefined,
    allocatedStock: number,
    selectedQuantity: number,
    availableQuantityToAdd: number | null,
): ReactElement {
    if (availableQuantityToAdd === 0) {
        return (
            <Text size="small" color="slt-red.1000">
                <FormattedMessage
                    id="quantity.maximum-available"
                    defaultMessage="Bag contains maximum available quantity."
                />
            </Text>
        );
    }

    if (availableQuantityToAdd && availableQuantityToAdd > 0) {
        return (
            <Text size="small" color="slt-red.1000">
                <FormattedMessage
                    id="quantity.not-available"
                    defaultMessage="{quantityInBag} items in bag, only {quantityAvailable} available"
                    values={{
                        quantityInBag: availableToSell - availableQuantityToAdd,
                        quantityAvailable: availableQuantityToAdd,
                    }}
                />
            </Text>
        );
    }

    return (
        <>
            {!hasEnoughStock ? (
                <AvailabilityMessage availableToSell={availableToSell} className="items-end" />
            ) : null}
            {includesItemsInBackorder && backorderInStockDate && !isOnlyBackOrder ? (
                <BackorderThresholdMessage
                    selectedQuantityInStock={allocatedStock}
                    selectedQuantityInBackorder={selectedQuantity - allocatedStock}
                    backorderInStockDate={backorderInStockDate}
                />
            ) : null}
        </>
    );
}

function renderBadges(
    availableToSell: number,
    isLimitedQuantityBadge: boolean | undefined,
    isOnlyXLeftBadge: boolean | undefined,
): ReactElement {
    return (
        <div className="flex flex-col items-end">
            {isLimitedQuantityBadge ? <LimitedQuantityLabel /> : null}
            {isOnlyXLeftBadge ? <OnlyXLeftLabel quantityLeft={availableToSell} /> : null}
        </div>
    );
}
